/**
 * ChallengesPage — Community food challenges with leaderboard
 * Route: /challenges
 */
import { useState, useEffect } from 'react';
import { ArrowLeft, Trophy, Users, Camera, Clock, Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function timeLeft(endsAt) {
  const diff = new Date(endsAt) - Date.now();
  if (diff <= 0) return 'Ended';
  const d = Math.floor(diff / 86400000);
  return d > 0 ? `${d}d left` : `${Math.floor(diff / 3600000)}h left`;
}

const ChallengesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const token = localStorage.getItem('fq_auth_token');
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  useEffect(() => {
    fetch(`${API_URL}/api/challenges`, { credentials: 'include', headers })
      .then(r => r.json()).then(d => setChallenges(d.challenges || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!title.trim()) return;
    await fetch(`${API_URL}/api/challenges`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...headers }, credentials: 'include',
      body: JSON.stringify({ title, description: desc }),
    });
    setShowCreate(false); setTitle(''); setDesc('');
    fetch(`${API_URL}/api/challenges`, { credentials: 'include', headers }).then(r => r.json()).then(d => setChallenges(d.challenges || []));
  };

  const handleJoin = async (id) => {
    await fetch(`${API_URL}/api/challenges/${id}/join`, { method: 'POST', credentials: 'include', headers });
    fetch(`${API_URL}/api/challenges`, { credentials: 'include', headers }).then(r => r.json()).then(d => setChallenges(d.challenges || []));
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 md:pb-8">
      <div className="relative" style={{ minHeight: '180px' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-pink-500 to-[#FF6B35]" />
        <div className="relative flex flex-col justify-end max-w-4xl mx-auto px-4" style={{ minHeight: '180px' }}>
          <div className="mb-5">
            <Link to="/" className="inline-flex items-center gap-1 text-white/70 hover:text-white text-xs mb-2"><ArrowLeft className="w-3 h-3" /> Back</Link>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3"><Trophy className="w-8 h-8" /> Food Challenges</h1>
            <p className="text-white/70 text-sm mt-1">Compete, share, win!</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-5">
        <div className="flex justify-between items-center mb-5">
          <span className="text-sm text-gray-500">{challenges.length} active challenges</span>
          {user && <button data-testid="create-challenge-btn" onClick={() => setShowCreate(!showCreate)} className="h-9 px-4 bg-gray-900 text-white rounded-2xl text-sm font-semibold flex items-center gap-1.5"><Plus className="w-4 h-4" />Create</button>}
        </div>

        {showCreate && (
          <div data-testid="challenge-create-form" className="bg-white rounded-[20px] p-5 mb-5 shadow-sm">
            <input data-testid="challenge-title-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Challenge title..." className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm mb-2 focus:outline-none" />
            <textarea data-testid="challenge-desc-input" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Describe the challenge..." rows={2} className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm mb-3 resize-none focus:outline-none" />
            <button data-testid="launch-challenge-btn" onClick={handleCreate} className="w-full py-2.5 bg-gray-900 text-white font-semibold text-sm rounded-2xl">Launch Challenge</button>
          </div>
        )}

        <div className="space-y-4">
          {loading ? [1,2,3].map(i => <div key={i} className="h-28 bg-gray-100 rounded-[20px] animate-pulse" />) :
          challenges.map(ch => (
            <div key={ch.challenge_id} className="bg-white rounded-[20px] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-base font-bold text-gray-900">{ch.title}</h3>
                  <p className="text-xs text-gray-500 line-clamp-2 mt-1">{ch.description}</p>
                </div>
                <span className="text-xs text-[#FF6B35] font-semibold flex items-center gap-1 whitespace-nowrap"><Clock className="w-3 h-3" />{timeLeft(ch.ends_at)}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{ch.participant_count} joined</span>
                  <span className="flex items-center gap-1"><Camera className="w-3 h-3" />{ch.entry_count} entries</span>
                </div>
                <button data-testid={`join-challenge-${ch.challenge_id}`} onClick={() => handleJoin(ch.challenge_id)} className="px-3 py-1 bg-gray-900 text-white text-xs font-semibold rounded-full">Join</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChallengesPage;
